/**
 * 2024 backgrounds. Each grants three ability score increases (+2/+1 or
 * +1/+1/+1), an Origin feat, two skill proficiencies, a tool proficiency,
 * and starting equipment.
 *
 * The SRD 5.2 backgrounds are CC-BY-4.0. Feylost and Witchlight Hand come from
 * The Wild Beyond the Witchlight, which is a 2014-rules book with no ability
 * increases or origin feats — only their skill and tool proficiencies are
 * original, and the 2024-shaped parts are marked `adapted` so the DM can
 * change them. Descriptions here are written fresh.
 */
import type { Ability } from '../constants'
import type { SkillKey } from '../constants'

export interface BackgroundDef {
  id: string
  name: string
  source: 'srd' | 'wbtw'
  tagline: string
  blurb: string
  /** The three abilities this background can raise. */
  abilities: [Ability, Ability, Ability]
  featId: string
  skills: [SkillKey, SkillKey]
  /** Tool proficiency, or a category the wizard asks about. */
  tool: { label: string; choiceOf?: string[] }
  equipment: string[]
  startingGold: number
  /** True where 2024-shaped details were inferred rather than published. */
  adapted?: boolean
  witchlightNote?: string
}

export const BACKGROUNDS_LIST: BackgroundDef[] = [
  {
    id: 'feylost',
    name: 'Feylost',
    source: 'wbtw',
    adapted: true,
    tagline: 'You wandered into the Feywild as a child and grew up there.',
    blurb:
      'The mortal world feels thin and strange to you now. You know how fey bargains work, which is exactly the knowledge this story rewards.',
    witchlightNote: 'Written for The Wild Beyond the Witchlight. Strongly recommended for this campaign.',
    abilities: ['dex', 'wis', 'cha'],
    featId: 'magic-initiate-druid',
    skills: ['deception', 'survival'],
    tool: { label: 'Musical Instrument of your choice' },
    equipment: ['Musical instrument', "Traveler's clothes", 'A trinket from the Feywild', 'Belt pouch'],
    startingGold: 8,
  },
  {
    id: 'witchlight-hand',
    name: 'Witchlight Hand',
    source: 'wbtw',
    adapted: true,
    tagline: 'You work the Witchlight Carnival — and it has kept you young.',
    blurb:
      'You run a booth, a ride, or a sideshow. Carnival folk know you on sight, and you know how the whole show fits together.',
    witchlightNote: 'Written for The Wild Beyond the Witchlight. Strongly recommended for this campaign.',
    abilities: ['dex', 'con', 'cha'],
    featId: 'musician',
    skills: ['performance', 'sleight_of_hand'],
    tool: { label: 'Disguise Kit' },
    equipment: ['Disguise kit', 'Musical instrument', 'Carnival uniform', 'Belt pouch'],
    startingGold: 8,
  },
  {
    id: 'acolyte',
    name: 'Acolyte',
    source: 'srd',
    tagline: 'You served in a temple and learned a little real magic.',
    blurb: 'Comfortable with ritual, hierarchy, and the things people pray about at 3am.',
    abilities: ['int', 'wis', 'cha'],
    featId: 'magic-initiate-cleric',
    skills: ['insight', 'religion'],
    tool: { label: "Calligrapher's Supplies" },
    equipment: ["Calligrapher's supplies", 'Holy symbol', 'Prayer book', 'Parchment (10 sheets)', 'Robe'],
    startingGold: 8,
  },
  {
    id: 'artisan',
    name: 'Artisan',
    source: 'srd',
    tagline: 'You learned a trade and can talk to anyone who buys from it.',
    blurb: 'Practical, sociable, and good at spotting when something was made badly on purpose.',
    abilities: ['str', 'dex', 'int'],
    featId: 'crafter',
    skills: ['investigation', 'persuasion'],
    tool: { label: "Artisan's Tools of your choice" },
    equipment: ["Artisan's tools", 'Pouch', "Traveler's clothes"],
    startingGold: 32,
  },
  {
    id: 'charlatan',
    name: 'Charlatan',
    source: 'srd',
    tagline: 'You have always been able to talk your way out.',
    blurb: 'Forgeries, false names, and a sincere face. Useful anywhere there are rules to bend.',
    abilities: ['dex', 'con', 'cha'],
    featId: 'skilled',
    skills: ['deception', 'sleight_of_hand'],
    tool: { label: 'Forgery Kit' },
    equipment: ['Forgery kit', 'Costume', 'Fine clothes'],
    startingGold: 15,
  },
  {
    id: 'criminal',
    name: 'Criminal',
    source: 'srd',
    tagline: 'You made your living outside the law.',
    blurb: 'Locks, shadows, and knowing which questions not to ask.',
    abilities: ['dex', 'con', 'int'],
    featId: 'alert',
    skills: ['sleight_of_hand', 'stealth'],
    tool: { label: "Thieves' Tools" },
    equipment: ["Thieves' tools", '2 Daggers', 'Pouch', "Traveler's clothes"],
    startingGold: 16,
  },
  {
    id: 'entertainer',
    name: 'Entertainer',
    source: 'srd',
    tagline: 'You perform, and people remember you.',
    blurb: 'A stage presence that works just as well on a crowd as on a guard captain.',
    abilities: ['str', 'dex', 'cha'],
    featId: 'musician',
    skills: ['acrobatics', 'performance'],
    tool: { label: 'Musical Instrument of your choice' },
    equipment: ['Musical instrument', 'Costume', 'Mirror', 'Perfume'],
    startingGold: 11,
  },
  {
    id: 'farmer',
    name: 'Farmer',
    source: 'srd',
    tagline: 'You worked the land, and it made you durable.',
    blurb: 'Steady, patient, and better with animals than most people are with people.',
    abilities: ['str', 'con', 'wis'],
    featId: 'tough',
    skills: ['animal_handling', 'nature'],
    tool: { label: "Carpenter's Tools" },
    equipment: ["Carpenter's tools", 'Sickle', 'Healer\'s kit', 'Iron pot', "Traveler's clothes"],
    startingGold: 30,
  },
  {
    id: 'guard',
    name: 'Guard',
    source: 'srd',
    tagline: 'You stood watch, and learned to read a crowd.',
    blurb: 'You notice the thing that is about to go wrong before anyone else does.',
    abilities: ['str', 'int', 'wis'],
    featId: 'alert',
    skills: ['athletics', 'perception'],
    tool: { label: 'Gaming Set of your choice' },
    equipment: ['Gaming set', 'Light crossbow with 20 bolts', 'Hooded lantern', 'Manacles', 'Quiver'],
    startingGold: 12,
  },
  {
    id: 'guide',
    name: 'Guide',
    source: 'srd',
    tagline: 'You lived in the wild and led others through it.',
    blurb: 'Quiet, self-sufficient, and never lost. A natural fit for a story that leaves the road.',
    abilities: ['dex', 'con', 'wis'],
    featId: 'magic-initiate-druid',
    skills: ['stealth', 'survival'],
    tool: { label: "Cartographer's Tools" },
    equipment: ["Cartographer's tools", 'Shortbow with 20 arrows', 'Bedroll', 'Tent', "Traveler's clothes"],
    startingGold: 3,
  },
  {
    id: 'hermit',
    name: 'Hermit',
    source: 'srd',
    tagline: 'You lived apart, and came back with something to say.',
    blurb: 'Solitude taught you medicine, faith, and a comfort with silence that unnerves people.',
    abilities: ['con', 'wis', 'cha'],
    featId: 'healer',
    skills: ['medicine', 'religion'],
    tool: { label: 'Herbalism Kit' },
    equipment: ['Herbalism kit', 'Quarterstaff', 'Bedroll', 'Book (philosophy)', 'Lamp', "Traveler's clothes"],
    startingGold: 16,
  },
  {
    id: 'merchant',
    name: 'Merchant',
    source: 'srd',
    tagline: 'You bought low, sold high, and travelled to do it.',
    blurb: 'You know what things are worth, including favours.',
    abilities: ['con', 'int', 'cha'],
    featId: 'lucky',
    skills: ['animal_handling', 'persuasion'],
    tool: { label: "Navigator's Tools" },
    equipment: ["Navigator's tools", 'Pouch', "Traveler's clothes"],
    startingGold: 22,
  },
  {
    id: 'noble',
    name: 'Noble',
    source: 'srd',
    tagline: 'You were raised with money and expectations.',
    blurb: 'Doors open for you. So do old grudges you did not personally earn.',
    abilities: ['str', 'int', 'cha'],
    featId: 'skilled',
    skills: ['history', 'persuasion'],
    tool: { label: 'Gaming Set of your choice' },
    equipment: ['Gaming set', 'Fine clothes', 'Perfume'],
    startingGold: 29,
  },
  {
    id: 'sage',
    name: 'Sage',
    source: 'srd',
    tagline: 'You studied, and the studying stuck.',
    blurb: 'You know things, and you know where to look up the things you do not.',
    abilities: ['con', 'int', 'wis'],
    featId: 'magic-initiate-wizard',
    skills: ['arcana', 'history'],
    tool: { label: "Calligrapher's Supplies" },
    equipment: ["Calligrapher's supplies", 'Book (history)', 'Parchment (8 sheets)', 'Quarterstaff', 'Robe'],
    startingGold: 8,
  },
  {
    id: 'sailor',
    name: 'Sailor',
    source: 'srd',
    tagline: 'You worked a ship, and could hold your own on the docks.',
    blurb: 'Rope, weather, and bar fights. Surprisingly transferable skills.',
    abilities: ['str', 'dex', 'wis'],
    featId: 'tavern-brawler',
    skills: ['acrobatics', 'perception'],
    tool: { label: "Navigator's Tools" },
    equipment: ["Navigator's tools", 'Dagger', 'Rope (50 feet)', "Traveler's clothes"],
    startingGold: 20,
  },
  {
    id: 'scribe',
    name: 'Scribe',
    source: 'srd',
    tagline: 'You copied documents, and read all of them.',
    blurb: 'Precise, observant, and very hard to fool with a forged letter.',
    abilities: ['dex', 'int', 'wis'],
    featId: 'skilled',
    skills: ['investigation', 'perception'],
    tool: { label: "Calligrapher's Supplies" },
    equipment: ["Calligrapher's supplies", 'Fine clothes', 'Lamp', 'Parchment (12 sheets)'],
    startingGold: 23,
  },
  {
    id: 'soldier',
    name: 'Soldier',
    source: 'srd',
    tagline: 'You served, and you know how a fight actually goes.',
    blurb: 'Discipline, drill, and the ability to be frightening on purpose.',
    abilities: ['str', 'dex', 'con'],
    featId: 'savage-attacker',
    skills: ['athletics', 'intimidation'],
    tool: { label: 'Gaming Set of your choice' },
    equipment: ['Gaming set', 'Spear', 'Shortbow with 20 arrows', 'Healer\'s kit', 'Quiver'],
    startingGold: 14,
  },
  {
    id: 'wayfarer',
    name: 'Wayfarer',
    source: 'srd',
    tagline: 'You grew up on the road with nothing guaranteed.',
    blurb: 'Street-smart, watchful, and used to landing on your feet.',
    abilities: ['dex', 'wis', 'cha'],
    featId: 'lucky',
    skills: ['insight', 'stealth'],
    tool: { label: "Thieves' Tools" },
    equipment: ["Thieves' tools", '2 Daggers', 'Bedroll', 'Pouch', "Traveler's clothes"],
    startingGold: 16,
  },
]

export const BACKGROUNDS_BY_ID = new Map(BACKGROUNDS_LIST.map((b) => [b.id, b]))

export const WITCHLIGHT_BACKGROUNDS = ['feylost', 'witchlight-hand']
